/**
 * Document Analysis Service
 * Handles all document analysis API calls
 */

import { apiClient } from './api.client';
import { DocumentAnalysis, GetAnalysesParams, AnalysisType } from '../types/api.types';

/**
 * Analyze a record deal document
 */
export const analyzeRecordDeal = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/record-deal', formData);
};

/**
 * Analyze a copyright agreement document
 */
export const analyzeCopyrightAgreement = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/copyright-agreement', formData);
};

/**
 * Analyze a booking/performance agreement document
 */
export const analyzeBookingPerformance = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/booking-performance', formData);
};

/**
 * Analyze a master license agreement document
 */
export const analyzeMasterLicense = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/master-license', formData);
};

/**
 * Analyze a media contract document
 */
export const analyzeMediaContract = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/media-contract', formData);
};

/**
 * Analyze a band partnership agreement document
 */
export const analyzeBandPartnership = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/band-partnership', formData);
};

/**
 * Analyze a joint venture agreement document
 */
export const analyzeJointVentureAgreement = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/joint-venture-agreement', formData);
};

/**
 * Analyze a letter of claim for copyright infringement undertakings
 */
export const analyzeLetterOfClaimCopyrightInfringement = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/letter-of-claim-copyright-infringement-undertakings', formData);
};

/**
 * Analyze a letter of claim for trademark infringement
 */
export const analyzeLetterOfClaimTrademarkInfringement = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/letter-of-claim-trademark-infringement', formData);
};

/**
 * Analyze a producer agreement document
 */
export const analyzeProducerAgreement = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/producer-agreement', formData);
};

/**
 * Analyze a production and representation agreement document
 */
export const analyzeProductionAndRepresentationAgreement = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/production-and-representation-agreement', formData);
};

/**
 * Analyze a remix agreement document
 */
export const analyzeRemixAgreement = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<string>('/api/read/remix-agreement', formData);
};

/**
 * Generic document analysis function
 * Routes to the appropriate analysis endpoint based on analysisType
 */
export const analyzeDocument = async (file: File, analysisType: AnalysisType): Promise<string> => {
  const analysisMap: Record<AnalysisType, (file: File) => Promise<string>> = {
    'record-deal': analyzeRecordDeal,
    'copyright-agreement': analyzeCopyrightAgreement,
    'booking-performance': analyzeBookingPerformance,
    'master-license': analyzeMasterLicense,
    'media-contract': analyzeMediaContract,
    'band-partnership': analyzeBandPartnership,
    'joint-venture-agreement': analyzeJointVentureAgreement,
    'letter-of-claim-copyright-infringement-undertakings': analyzeLetterOfClaimCopyrightInfringement,
    'letter-of-claim-trademark-infringement': analyzeLetterOfClaimTrademarkInfringement,
    'producer-agreement': analyzeProducerAgreement,
    'production-and-representation-agreement': analyzeProductionAndRepresentationAgreement,
    'remix-agreement': analyzeRemixAgreement,
  };

  const analysisFn = analysisMap[analysisType];
  if (!analysisFn) {
    throw new Error(`Unknown analysis type: ${analysisType}`);
  }

  return analysisFn(file);
};

/**
 * Get user's document analyses
 */
export const getUserAnalyses = async (params: GetAnalysesParams): Promise<DocumentAnalysis[]> => {
  const queryParams = new URLSearchParams();
  if (params.analysisType) {
    queryParams.append('analysisType', params.analysisType);
  }
  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    queryParams.append('offset', params.offset.toString());
  }

  const endpoint = `/api/read/analyses${queryParams.toString() ? `?${queryParams}` : ''}`;
  return apiClient.get<DocumentAnalysis[]>(endpoint);
};

/**
 * Get a specific document analysis by ID
 */
export const getAnalysisById = async (id: string): Promise<DocumentAnalysis> => {
  return apiClient.get<DocumentAnalysis>(`/api/read/analyses/${id}`);
};

/**
 * Delete a document analysis
 */
export const deleteAnalysis = async (id: string): Promise<void> => {
  return apiClient.delete<void>(`/api/read/analyses/${id}`);
};

/**
 * Submit a star rating for an analysis
 */
export const submitAnalysisRating = async (
  id: string,
  rating: number,
  comment?: string,
): Promise<void> => {
  return apiClient.patch<void>(`/api/read/analyses/${id}/rating`, { rating, comment });
};
 